import {
  computed,
  getCurrentScope,
  onScopeDispose,
  reactive,
  toRef,
} from "vue";

import { useAudioFocus } from "@/composables/use-audio-focus.composable";
import { useHtmlAudio } from "@/composables/use-html-audio.composable";
import { useMediaSession } from "@/composables/use-media-session.composable";
import type { Track } from "@/types";

export type { PlaybackStatus } from "@/composables/use-html-audio.composable";

export type RepeatMode = "none" | "queue" | "track";

type PlaybackOptions = {
  autoAdvance: boolean;
  repeatMode: RepeatMode;
};

type State = {
  tracks: Track[];
  currentIndex: number;
  options: PlaybackOptions;
};

type Options = {
  transformSourceUrl?: (sourceUrl: string) => string;
  onError?: (error: Error) => void;
};

type SetTracklistOptions = {
  autoplay?: boolean;
  initialIndex?: number;
  startAtSeconds?: number;
};

/**
 * Creates a player bound to the active Vue effect scope.
 *
 * @throws When called without an active Vue effect scope.
 */
export function usePlayer({ transformSourceUrl, onError }: Options = {}) {
  if (!getCurrentScope()) {
    throw new Error("usePlayer() requires an active Vue effect scope");
  }

  const audioFocus = useAudioFocus();
  const state = reactive<State>({
    tracks: [],
    currentIndex: 0,
    options: {
      autoAdvance: true,
      repeatMode: "none",
    },
  });
  const abortController = new AbortController();

  const currentTrack = computed(() => state.tracks[state.currentIndex]);
  const nextIndex = computed(() => {
    const index = state.currentIndex + 1;

    if (state.tracks[index]) {
      return index;
    }

    return state.options.repeatMode === "queue" && state.tracks.length
      ? 0
      : undefined;
  });
  const previousIndex = computed(() => {
    const index = state.currentIndex - 1;

    if (state.tracks[index]) {
      return index;
    }

    return state.options.repeatMode === "queue" && state.tracks.length
      ? state.tracks.length - 1
      : undefined;
  });
  const nextTrack = computed(() => {
    const index = nextIndex.value;

    return index === undefined ? undefined : state.tracks[index];
  });
  const prevTrack = computed(() => {
    const index = previousIndex.value;

    return index === undefined ? undefined : state.tracks[index];
  });

  const htmlAudio = useHtmlAudio({
    signal: abortController.signal,
    transformSourceUrl,
    onBeforePlay() {
      audioFocus.claim(player);
    },
    onEnded: handleEnded,
    onError,
  });

  function handleEnded() {
    if (!currentTrack.value || !state.options.autoAdvance) {
      htmlAudio.pause();
      return;
    }

    if (state.options.repeatMode === "track") {
      htmlAudio.seekTo(0);
      void play();
      return;
    }

    const index = nextIndex.value;

    if (index === undefined) {
      htmlAudio.pause();
      return;
    }

    void skipTo(index);
  }

  async function skipTo(index: number) {
    const track = state.tracks[index];

    if (!track) {
      return;
    }

    if (index === state.currentIndex) {
      htmlAudio.seekTo(0);
      await play();
      return;
    }

    state.currentIndex = index;
    htmlAudio.load(track.audioUrl, { startAtSeconds: 0 });
    await play();
  }

  async function play() {
    const track = currentTrack.value;

    if (!track) {
      return;
    }

    if (htmlAudio.ended.value) {
      if (state.options.repeatMode === "track") {
        htmlAudio.seekTo(0);
        await htmlAudio.play(track.audioUrl);
        return;
      }

      await skipTo(nextIndex.value ?? 0);
      return;
    }

    await htmlAudio.play(track.audioUrl);
  }

  function toggle() {
    if (htmlAudio.isPlaybackRequested.value) {
      htmlAudio.pause();
      return Promise.resolve();
    }

    return play();
  }

  function seekForward(offset = 10) {
    htmlAudio.seekBy(offset);
  }

  function seekBackward(offset = 10) {
    htmlAudio.seekBy(-offset);
  }

  function skipForward() {
    const index = nextIndex.value;

    return index === undefined ? Promise.resolve() : skipTo(index);
  }

  function skipBackward() {
    const index = previousIndex.value;

    return index === undefined ? Promise.resolve() : skipTo(index);
  }

  function cycleRepeatMode() {
    const transitions = {
      none: "queue",
      queue: "track",
      track: "none",
    } as const;

    state.options.repeatMode = transitions[state.options.repeatMode];
  }

  function setTracklist(
    tracks: readonly Track[],
    {
      autoplay = false,
      initialIndex = 0,
      startAtSeconds,
    }: SetTracklistOptions = {},
  ) {
    const nextTracks = [...tracks];
    const selectedIndex = nextTracks[initialIndex] ? initialIndex : 0;
    const track = nextTracks[selectedIndex];

    if (!track) {
      htmlAudio.unload();
      state.tracks = nextTracks;
      state.currentIndex = selectedIndex;
      audioFocus.release(player);
      return;
    }

    state.tracks = nextTracks;
    state.currentIndex = selectedIndex;
    htmlAudio.load(track.audioUrl, {
      startAtSeconds,
      autoplay,
    });
  }

  function clearTracklist() {
    htmlAudio.unload();
    state.tracks = [];
    state.currentIndex = 0;
    audioFocus.release(player);
  }

  const tracks = toRef(state, "tracks");
  const options = toRef(state, "options");

  const player = {
    tracks,
    options,
    status: htmlAudio.status,
    isPlaybackRequested: htmlAudio.isPlaybackRequested,
    timeElapsed: htmlAudio.timeElapsed,
    duration: htmlAudio.duration,
    buffered: htmlAudio.buffered,
    position: htmlAudio.position,
    ended: htmlAudio.ended,
    currentTrack,
    nextTrack,
    prevTrack,
    cycleRepeatMode,
    setTracklist,
    clearTracklist,
    play,
    pause: htmlAudio.pause,
    toggle,
    seekTo: htmlAudio.seekTo,
    seekForward,
    seekBackward,
    skipTo,
    skipForward,
    skipBackward,
  };

  useMediaSession(player);

  onScopeDispose(() => {
    abortController.abort();
    audioFocus.release(player);
  });

  return player;
}

export type UsePlayerReturn = ReturnType<typeof usePlayer>;
